import { domAnimation, LazyMotion } from 'framer-motion';
import uniqueId from 'lodash/uniqueId';
import React, { useMemo, useState } from 'react';
import { useEffectOnce } from 'usehooks-ts';

import Badge from 'components/Badge';
import Button from 'components/Button/Button';
import MultiSection from 'components/MultiSection/MultiSection';
import Overlay from 'components/Overlay/Overlay';
import CapabilitiesOverlay from 'components/Overlays/CapabilitiesOverlay/CapabilitiesOverlay';
import CaseStudyOverlay from 'components/Overlays/CaseStudyOverlay/CaseStudyOverlay';
import Text from 'components/Text/Text';
import useOverElement from 'hooks/useOverElement';
import { useCopyStore, useGlobalStore } from 'store';
import { CopyStoreType } from 'store/copy.types';
import { pageMotionProps } from 'utils/styles/animations';

import * as A from './CaseStudyPage.animations';
import { CaseStudy } from './CaseStudyPage.data';

import * as S from './CaseStudyPage.styles';

interface CaseStudyPageProps {
  caseStudy: CaseStudy;
  initialCopy: CopyStoreType['copy'];
}

const moduleComponents = {
  ComponentSectionsMultiSectionBlock: MultiSection,
  ComponentComponentsMediaCarousel: S.CaseStudyMediaSlider,
  ComponentSectionsQuoteSection: S.CaseStudyQuoteSection,
};

const FULL_WIDTH_SECTIONS = ['ComponentSectionsQuoteSection'];

const CaseStudyPage: React.FunctionComponent<CaseStudyPageProps> = ({
  caseStudy,
  initialCopy,
}) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const { headerText: headerTextStore, setHeaderText } = useGlobalStore();
  const { setCopy } = useCopyStore();

  const [heroRef] = useOverElement();

  const [capabilitiesOverlay, setCapabilitiesOverlay] = useState(undefined);
  const keyPairs = caseStudy.introPanel?.keyPair;

  useEffectOnce(() => {
    setCopy(initialCopy);
    setHeaderText({
      ...headerTextStore,
      normal: caseStudy?.headerText,
      colored: caseStudy?.headerColoredText,
      color: caseStudy?.headerColor,
    });
  });

  const hero = (
    <S.HeroWrapper ref={heroRef}>
      <S.HeadlineComp text={caseStudy.hero?.description.title} size="display" />
      <S.HeroMedia media={caseStudy.hero || []} {...caseStudy.hero} />
      <S.RichTextComp richText={caseStudy.hero?.description.subtitle} />
    </S.HeroWrapper>
  );

  const leftSideIntro = (
    <S.CaseStudyLeftSideIntro>
      <Button
        label={caseStudy.introPanel?.ctaButton?.buttonLabel}
        href={caseStudy.introPanel?.ctaButton?.href || null}
        onClick={() =>
          !caseStudy.introPanel?.ctaButton?.href && setShowOverlay(true)
        }
      />
    </S.CaseStudyLeftSideIntro>
  );

  const intro = (
    <LazyMotion features={domAnimation} key={uniqueId()}>
      <S.CaseStudyInfoSectionGrid>
        {caseStudy.introPanel?.ctaButton && leftSideIntro}
        <S.IntroWrapper>
          <S.IntroSectionText text={caseStudy.introPanel?.title} size="body" />

          {(keyPairs || []).map(({ field, category }) => (
            <S.IntroSectionListWrapper key={field}>
              <Text text={category} size="filter" />

              <S.IntroSectionList>
                {field.split(', ').map(item => (
                  <S.IntroSectionListItem key={item}>
                    <Badge>
                      <Text text={item} size="small" />
                    </Badge>
                  </S.IntroSectionListItem>
                ))}
              </S.IntroSectionList>
            </S.IntroSectionListWrapper>
          ))}
        </S.IntroWrapper>
      </S.CaseStudyInfoSectionGrid>
    </LazyMotion>
  );

  const moduleSections = useMemo(
    () =>
      (caseStudy.modules || []).map(moduleData => {
        const Component = moduleComponents[moduleData.__typename];
        if (!Component) return null;
        return (
          <LazyMotion features={domAnimation} key={uniqueId()}>
            <S.CaseStudySectionGrid
              fullWidth={FULL_WIDTH_SECTIONS.includes(moduleData.__typename)}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={A.CaseStudyPageVariants}
            >
              <Component
                key={uniqueId()}
                {...moduleData}
                overlay={caseStudy.introPanel.caseStudyOverlay}
                caseStudy={caseStudy}
              />
            </S.CaseStudySectionGrid>
          </LazyMotion>
        );
      }),
    [caseStudy]
  );

  return (
    <S.Wrapper {...pageMotionProps}>
      {hero}
      {intro}

      {moduleSections}

      <Overlay
        isOpen={!!capabilitiesOverlay}
        onExitClick={() => setCapabilitiesOverlay(undefined)}
      >
        <CapabilitiesOverlay {...capabilitiesOverlay} />
      </Overlay>

      <Overlay isOpen={showOverlay} onExitClick={() => setShowOverlay(false)}>
        <CaseStudyOverlay
          {...caseStudy.introPanel?.caseStudyOverlay}
          caseStudy={caseStudy}
        />
      </Overlay>
    </S.Wrapper>
  );
};

export default CaseStudyPage;
